import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, RotateCcw, Search, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, api, inr, critTone, actionTone } from "@/lib/mro";

export default function DataPage({ plant }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [replace, setReplace] = useState(false);
  const [audit, setAudit] = useState([]);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    const [m, a] = await Promise.all([
      api.get("/materials", { params: { plant, search: search || undefined } }),
      api.get("/audit", { params: { limit: 25 } }),
    ]);
    setRows(m.data.materials);
    setAudit(a.data.logs);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [plant]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/upload/csv?replace=${replace}`, fd);
      toast.success(`${r.data.inserted} rows ingested`, { description: `Dataset now has ${r.data.total_lines} lines` });
      load();
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || "Check the file format" });
    }
    e.target.value = "";
  };

  const reseed = async () => {
    await api.post("/seed/reset");
    toast.success("Demo dataset restored");
    load();
  };

  return (
    <div className="space-y-5" data-testid="data-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Material master & data ingestion</h1>
        <p className="text-sm text-muted-foreground">Run the demo on the seeded SAP-like dataset, or upload your own extract. Descriptions are parsed for manufacturer, part number and specification on ingest.</p>
      </div>

      <div className="panel p-5" data-testid="upload-panel">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} data-testid="csv-input" />
          <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()}><Upload className="mr-1.5 h-4 w-4" />Upload CSV extract</Button>
          <Button variant="outline" data-testid="template-btn" onClick={() => window.open(`${API}/upload/template`, "_blank")}>
            <Download className="mr-1.5 h-4 w-4" />Download template
          </Button>
          <Button variant="outline" data-testid="export-btn" onClick={() => window.open(`${API}/export/csv?plant=${encodeURIComponent(plant)}`, "_blank")}>
            <Download className="mr-1.5 h-4 w-4" />Export full dataset
          </Button>
          <div className="flex items-center gap-2">
            <Switch id="replace" data-testid="replace-toggle" checked={replace} onCheckedChange={setReplace} />
            <Label htmlFor="replace" className="text-xs">Replace existing dataset</Label>
          </div>
          <Button variant="ghost" data-testid="data-reseed" onClick={reseed}><RotateCcw className="mr-1.5 h-4 w-4" />Restore demo data</Button>
        </div>
        <p className="mt-3 font-mono text-[11px] text-muted-foreground">
          Required columns: description, plant, unrestricted_qty, unit_price · Optional: material_code, manufacturer, part_number, specification, category, criticality, equipment, lead_time_days, vendor, vendor_otif_pct, moq, current_safety_stock, months_since_movement, m1…m12
        </p>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="material-search" className="pl-9" placeholder="Search materials…" value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <Button variant="outline" data-testid="material-search-btn" onClick={load}>Search</Button>
      </div>

      <div className="panel overflow-hidden" data-testid="materials-table">
        <div className="border-b border-border px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{rows.length} stocking lines</div>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Plant</th>
                <th className="px-4 py-2.5">Crit.</th><th className="px-4 py-2.5 text-right">Qty</th>
                <th className="px-4 py-2.5 text-right">SS</th><th className="px-4 py-2.5 text-right">Value</th><th className="px-4 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`material-row-${r.id}`} className="cursor-pointer border-t border-border hover:bg-accent/40" onClick={() => navigate(`/part/${r.id}`)}>
                  <td className="px-4 py-2.5"><div className="font-mono">{r.description}</div><div className="text-[11px] text-muted-foreground">{r.canonical_name}</div></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.material_code}</td>
                  <td className="px-4 py-2.5">{r.plant}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className={critTone(r.criticality)}>{r.criticality}</Badge></td>
                  <td className="px-4 py-2.5 text-right font-mono tabular">{r.unrestricted_qty}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular">{r.current_safety_stock} → {r.recommended_safety_stock}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular">{inr(r.inventory_value)}</td>
                  <td className="px-4 py-2.5">{r.action && <Badge variant="outline" className={actionTone(r.action)}>{r.action}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel p-5" data-testid="audit-panel">
        <h3 className="font-display text-sm font-semibold">Governance & action audit trail</h3>
        <p className="text-xs text-muted-foreground">Every SME decision and planner action is versioned and timestamped.</p>
        <div className="mt-3 space-y-1.5">
          {audit.length === 0 && <div className="text-sm text-muted-foreground">No actions recorded yet.</div>}
          {audit.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 font-mono text-[11px]">
              <Badge variant="outline" className="text-[10px]">{l.type}</Badge>
              <span className="font-semibold">{l.action}</span>
              <span className="text-muted-foreground">{l.entity}</span>
              <span className="text-muted-foreground">· {l.actor}</span>
              <span className="ml-auto text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
              {l.note && <div className="w-full text-muted-foreground">{l.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
